import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function TrailerISverige() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Hastighed og kørsel", path: "/trailer-hastighed" },
    { name: "Trailer i Sverige", path: "/trailer-i-sverige" }
  ];

  const tocItems = [
    { id: "hastighed-sverige", title: "Hastighedsgrænser med trailer i Sverige" },
    { id: "vinterdaek-regler", title: "Særlige regler for vinterdæk og pigdæk" },
    { id: "ubremset-trailer", title: "Særligt om ubremsede trailere (40 km/t-grænsen)" },
    { id: "tempo-100-ugyldig", title: "Ingen Tempo 100 i Sverige" },
    { id: "krav-til-danske-trailere", title: "Dokumenter og udstyr for danske trailere" },
    { id: "boeder-og-kontrol", title: "Politiets kontrol og hastighedsbøder" }
  ];

  const faq = [
    {
      q: "Hvor hurtigt må man køre med trailer på motorvej i Sverige?",
      a: "I Sverige må en almindelig personbil med bremset trailer maksimalt køre 80 km/t på både motorvej og landevej. Hastighedsgrænsen på 80 km/t gælder, selv om motorvejens skilte tillader 110 eller 120 km/t for personbiler uden påhæng."
    },
    {
      q: "Skal traileren have vinterdæk i Sverige?",
      a: "Ja. I perioden fra 1. december til 31. marts er der lovkrav om vinterdæk ved vintervejr (is, sne, sjap eller frost). Hvis trækbilen er monteret med vinterdæk, skal traileren ligeledes have monteret vinterdæk."
    },
    {
      q: "Må bilen køre med pigdæk, hvis traileren kører med almindelige vinterdæk?",
      a: "Nej. Ifølge den svenske Trafikförordning gælder en asymmetrisk regel: Hvis trækbilen kører med pigdæk (dubbade däck), SKAL traileren også være udstyret med pigdæk. Hvis bilen derimod kører med pigfri vinterdæk, må traileren køre med enten pigdæk eller pigfri vinterdæk."
    },
    {
      q: "Hvorfor må nogle ubremsede trailere kun køre 40 km/t i Sverige?",
      a: "Hvis en ubremset trailer har en tilladt totalvægt, der overstiger halvdelen af trækbilens tjenstevægt (eller hvis traileren er uaffjedret), falder den lovlige hastighedsgrænse automatisk til 40 km/t i henhold til Trafikförordningen kapitel 4 § 20."
    },
    {
      q: "Gælder en dansk Tempo 100-godkendelse i Sverige?",
      a: "Nej. Svensk færdselslovgivning anerkender hverken den danske eller den tyske Tempo 100-ordning for almindelige trailere eller campingvogne. Den maksimale tilladte hastighed er altid 80 km/t."
    }
  ];

  const sources = [
    { title: "Trafikförordning (1998:1276) 4 kap. 20 § om hastighet med släp", source: "riksdagen.se", url: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/trafikforordning-19981276_sfs-1998-1276/" },
    { title: "Færdselsstyrelsen: Krav og regler for Tempo 100 påhængskøretøjer", source: "fstyr.dk", url: "https://fstyr.dk/privat/krav-til-koeretoejer/tempo-100" }
  ];

  return (
    <>
      <SEO 
        title="Trailer i Sverige: Hastighed, vinterdæk og regler" 
        description="Regler for kørsel med trailer i Sverige: fartgrænse 80 km/t, vinterdækkrav, regler for pigdæk samt 40 km/t-grænsen for ubremsede trailere." 
        pathname="/trailer-i-sverige"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />

        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Trailer i Sverige: Hastighed, vinterdæk og regler
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          I Sverige er den maksimale tilladte hastighed for en personbil med bremset trailer <strong>80 km/t på både motorvej og landevej</strong>. Der findes ingen Tempo 100-ordning for påhængsvogne på svenske veje.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          I vinterperioden stiller de svenske myndigheder markant skrappere krav til dækudrustning end i Danmark: Ved vinterføre skal traileren have vinterdæk, hvis bilen har det. Kører trækbilen på pigdæk, er det et ufravigeligt lovkrav, at traileren ligeledes er udstyret med pigdæk.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Vigtigste regler for trailerkørsel i Sverige">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>• Maksimal hastighed på motorvej: <strong>80 km/t</strong></li>
            <li>• Tempo 100-mærker (DK/DE): <strong>Ugyldige i Sverige</strong></li>
            <li>• Vinterdæksæson: <strong>1. december – 31. marts ved vinterføre</strong></li>
            <li>• Pigdæk-krav: <strong>Har bilen pigdæk, skal traileren have pigdæk</strong></li>
          </ul>
        </Callout>

        <h2 id="hastighed-sverige" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hastighedsgrænser med trailer i Sverige
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I henhold til den svenske Trafikförordning (1998:1276) kapitel 4 § 20 gælder faste maksimale hastighedsgrænser for bil med påhængskøretøj:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Køretøjskombination</th>
                <th className="py-2.5 pr-4 font-semibold">Tätort (byzone)</th>
                <th className="py-2.5 pr-4 font-semibold">Landsväg (landevej)</th>
                <th className="py-2.5 font-semibold">Motorväg (motorvej)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Bremset trailer</td>
                <td className="py-3 pr-4">50 km/t (el. skiltet)</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">80 km/t</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">80 km/t</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Ubremset (totalvægt &le; halvdelen af bilens tjenstevægt, maks. 750 kg)</td>
                <td className="py-3 pr-4">50 km/t (el. skiltet)</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">80 km/t</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">80 km/t</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Ubremset (totalvægt &gt; halvdelen af bilens tjenstevægt)</td>
                <td className="py-3 pr-4">40 km/t</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">40 km/t</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">40 km/t</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Uaffjedret trailer</td>
                <td className="py-3 pr-4">40 km/t</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">40 km/t</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">40 km/t</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Selvom den generelle hastighedsgrænse på en svensk motorvej kan være skiltet til 110 eller 120 km/t, gælder den særlige køretøjsbestemte grænse på 80 km/t altid for vogntoget. Overskridelse udløser hastighedsbøder efter de normale svenske takster.
        </p>

        <h2 id="vinterdaek-regler" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Særlige regler for vinterdæk og pigdæk
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I Sverige gælder kravet om vinterdæk fra <strong>1. december til 31. marts</strong>, når der er vintervejr (vinterväglag). Vintervejr defineres af svensk politi som forekomst af sne, is, sjap eller frostglatte vejbaner på nogen del af vejstrækningen.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Ved kørsel med trailer gælder følgende specifikke kombinationskrav:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Trækbilens dækmontering</th>
                <th className="py-2.5 pr-4 font-semibold">Trailerens tilladte dækmontering</th>
                <th className="py-2.5 font-semibold">Lovlighed ved vinterføre</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Pigdæk (dubbade däck)</td>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Skal have pigdæk</td>
                <td className="py-3 text-[#5C636B]">Ufravigeligt krav i svensk ret</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Pigfri vinterdæk (friktionsdäck)</td>
                <td className="py-3 pr-4">Pigdæk eller pigfri vinterdæk</td>
                <td className="py-3 text-[#5C636B]">Begge kombinationer er lovlige</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Sommerdæk (kun v. tør vej)</td>
                <td className="py-3 pr-4">Sommerdæk tilladt (uden for vinterføre)</td>
                <td className="py-3 text-[#5C636B]">Ulovligt, så snart der optræder vinterføre</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Kravet til mønsterdybde på en let trailer (tilladt totalvægt op til 3.500 kg) er mindst 1,6 mm, men de svenske myndigheder anbefaler kraftigt mindst 3,0 mm på samtlige hjul for at bevare retningsstabiliteten på glatte vinterveje. Læs mere om dæk i <Link to="/trailer-daek" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer dæk</Link>.
        </p>

        <h2 id="ubremset-trailer" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Særligt om ubremsede trailere (40 km/t-grænsen)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Mange danske bilister med almindelige havevogne bliver overraskede over den svenske særregel for ubremsede trailere:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Hvis traileren <strong>ikke har bremser</strong>, må den kun køre 80 km/t, såfremt trailerens totalvægt ikke overstiger halvdelen af bilens tjenstevægt (køreklar vægt) og højst 750 kg.</li>
          <li>Kører du med en lille bil, hvor trailerens tilladte totalvægt overstiger denne halvdel, er den maksimale lovlige hastighed i hele Sverige kun <strong>40 km/t</strong>.</li>
          <li>Er traileren uaffjedret, gælder 40 km/t-grænsen ligeledes altid.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Tjek altid bilens og trailerens registreringsattester grundigt, inden du krydser Øresundsbroen eller tager færgen til Sverige. Læs mere om bremseregler under <Link to="/trailer-bremser" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer med og uden bremser</Link>.
        </p>

        <h2 id="tempo-100-ugyldig" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Ingen Tempo 100 i Sverige
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I Danmark og Tyskland findes der velfungerende nationale Tempo 100-ordninger, hvor godkendte trailere og campingvogne må køre 100 km/t på motorvej.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Sverige har <strong>ikke indført nogen Tempo 100-ordning</strong> for personbiler med påhængskøretøj. Hverken et dansk Tempo 100-klistermærke eller en tysk Tempo 100-godkendelse giver ret til at køre hurtigere end 80 km/t i Sverige. Kører du 100 km/t på en svensk motorvej, betragtes det juridisk som en hastighedsoverskridelse på 20 km/t. Læs om godkendelseskrav i <Link to="/tempo-100" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">Tempo 100</Link>.
        </p>

        <h2 id="krav-til-danske-trailere" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Dokumenter og udstyr for danske trailere
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når du kører med en dansk indregistreret trailer i Sverige, skal du medbringe følgende i bilen:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Original registreringsattest (del 1):</strong> Du skal medbringe den originale del 1 af registreringsattesten for både bil og trailer. En fotokopi eller digital app er ikke tilstrækkelig ved svensk politi- eller toldkontrol.</li>
          <li><strong>Nationalitetsmærke:</strong> Hvis trailerens nummerplade ikke har den blå EU-stribe med „DK“, skal der være monteret et godkendt ovalt DK-nationalitetsmærke bagpå traileren.</li>
          <li><strong>Advarselstrekant:</strong> Skal medbringes i bilen og opstilles ved standsning eller uheld på vejen i god afstand bag vogntoget.</li>
          <li><strong>Sikkerhedswire:</strong> Skal være koblet korrekt til trækbilens faste øje eller krog i henhold til gældende sikkerhedskrav.</li>
        </ul>

        <h2 id="boeder-og-kontrol" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Politiets kontrol og hastighedsbøder
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Det svenske politi (Polismyndigheten) foretager jævnlige kontroller af vogntog på de store indfaldsveje, herunder E6 og E4. Hastighedsbøder fastsættes som standardbøder og stiger progressivt ved højere overskridelser.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Overskrides hastighedsgrænsen med mere end 30 km/t på en 80 km/t-strækning (dvs. kørsel med 111 km/t eller derover), risikerer føreren øjeblikkelig frakendelse af førerretten i Sverige samt indberetning til de danske myndigheder. Læs mere om regler i andre lande under <Link to="/trailer-i-udlandet" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer i udlandet</Link> og <Link to="/trailer-hastighed" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer hastighed</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
